import * as React from 'react';
import * as api from '../services/apiService';
import type { User, UserRole } from '../types';
import { useUI } from './UIProvider';

const { createContext, useState, useCallback, useContext, useEffect, useRef } = React;

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

interface AuthContextState {
  currentUser: User | null;
  managedProfile: User | null;
  users: User[];
  authStatus: AuthStatus;
  login: (handle: string, password: string) => Promise<{error: string} | User>;
  loginWithGoogle: (idToken: string) => Promise<{error: string} | User>;
  loginWithFacebook: (accessToken: string) => Promise<{error: string} | User>;
  register: (name: string, handle: string, email: string, password: string) => Promise<{error: string} | User>;
  logout: () => void;
  startManaging: (profile: User) => void;
  stopManaging: () => void;
  completeOnboarding: (roles: UserRole[], referralCode?: string, newHandle?: string) => Promise<User | void>;
  handleUpdateProfile: (updatedUser: User) => Promise<User>;
  handleEndorseUser: (targetUserHandle: string) => Promise<void>;
  handleUnendorseUser: (targetUserHandle: string) => Promise<void>;
  handleToggleUserFan: (targetHandle: string) => Promise<void>;
  handleChangePassword: (currentPassword: string, newPassword: string) => Promise<{error?: string}>;
  resendVerification: () => Promise<{error?: string}>;
  hasMoreUsers: boolean;
  isLoadingMoreUsers: boolean;
  loadMoreUsers: () => Promise<void>;
  refreshUsers: () => Promise<void>;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const AuthContext = createContext<AuthContextState | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [authStatus, setAuthStatus] = useState<AuthStatus>('checking');
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [managedProfile, setManagedProfile] = useState<User | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [hasMoreUsers, setHasMoreUsers] = useState(false);
    const [isLoadingMoreUsers, setIsLoadingMoreUsers] = useState(false);
    const lastUserDocIdRef = useRef<string | null>(null);
    const { showToast } = useUI();
    const hasFetchedInitialData = useRef(false);

    const setUnauthenticatedState = useCallback(() => {
        localStorage.removeItem('youramp-token');
        sessionStorage.removeItem('youramp-nav-state');
        setCurrentUser(null);
        setManagedProfile(null);
        setUsers([]);
        setHasMoreUsers(false);
        lastUserDocIdRef.current = null;
        setAuthStatus('unauthenticated');
    }, []);

    const logout = useCallback(() => {
        setUnauthenticatedState();
    }, [setUnauthenticatedState]);

    const refreshUsers = useCallback(async () => {
        try {
            lastUserDocIdRef.current = null;
            const [result, currentUserData] = await Promise.all([api.getUsers(), api.getCurrentUser()]);
            setUsers(result.users);
            lastUserDocIdRef.current = result.lastDocId;
            setHasMoreUsers(result.hasMore);
            setCurrentUser(currentUserData);
        } catch (error: any) {
            if (error.status === 401 || error.status === 403) {
                logout();
            } else {
                console.error("Failed to refresh user data", error);
            }
        }
    }, [logout]);

    const loadMoreUsers = useCallback(async () => {
        if (isLoadingMoreUsers || !hasMoreUsers) return;
        setIsLoadingMoreUsers(true);
        try {
            const result = await api.getUsers(lastUserDocIdRef.current || undefined);
            setUsers(prev => {
                const existingHandles = new Set(prev.map(u => u.handle));
                const uniqueNew = result.users.filter(u => !existingHandles.has(u.handle));
                return [...prev, ...uniqueNew];
            });
            lastUserDocIdRef.current = result.lastDocId;
            setHasMoreUsers(result.hasMore);
        } catch (error) {
            console.error("Failed to load more users", error);
        } finally {
            setIsLoadingMoreUsers(false);
        }
    }, [isLoadingMoreUsers, hasMoreUsers]);

    const login = useCallback(async (handle: string, password: string) => {
        try {
            const result = await api.login(handle, password);
            localStorage.setItem('youramp-token', result.token);
            setCurrentUser(result.user);
            setAuthStatus('authenticated');
            return result.user;
        } catch (error: any) {
            setUnauthenticatedState();
            return { error: error.message };
        }
    }, [setUnauthenticatedState]);

    const loginWithGoogle = useCallback(async (idToken: string) => {
        try {
            const result = await api.loginWithGoogle(idToken);
            localStorage.setItem('youramp-token', result.token);
            setCurrentUser(result.user);
            setAuthStatus('authenticated');
            return result.user;
        } catch (error: any) {
            setUnauthenticatedState();
            return { error: error.message };
        }
    }, [setUnauthenticatedState]);

    const loginWithFacebook = useCallback(async (accessToken: string) => {
        try {
            const result = await api.loginWithFacebook(accessToken);
            localStorage.setItem('youramp-token', result.token);
            setCurrentUser(result.user);
            setAuthStatus('authenticated');
            return result.user;
        } catch (error: any) {
            setUnauthenticatedState();
            return { error: error.message };
        }
    }, [setUnauthenticatedState]);

    const register = useCallback(async (name: string, handle: string, email: string, password: string) => {
        try {
            const result = await api.register(name, handle, email, password);
            localStorage.setItem('youramp-token', result.token);
            setCurrentUser(result.user);
            setAuthStatus('authenticated');
            return result.user;
        } catch (error: any) {
            setUnauthenticatedState();
            return { error: error.message };
        }
    }, [setUnauthenticatedState]);

    const completeOnboarding = useCallback(async (roles: UserRole[], referralCode?: string, newHandle?: string) => {
        try {
            const result = await api.completeOnboarding(roles as any, referralCode, newHandle);
            const user = result.user;
            if (result.token) {
                localStorage.setItem('youramp-token', result.token);
            }
            setCurrentUser(user);
            showToast("Welcome! Your profile is set up.");
            return user;
        } catch (error: any) {
            showToast(`Onboarding failed: ${error.message}`);
            throw error;
        }
    }, [showToast]);
    
    const checkSession = useCallback(async () => {
      const token = localStorage.getItem('youramp-token');
      // If token is missing or 'null'/'undefined' string, just go to unauthenticated
      if (!token || token === 'null' || token === 'undefined') {
          setUnauthenticatedState();
          return;
      }
      
      try {
          const userData = await api.getCurrentUser();
          if (userData) {
              setCurrentUser(userData);
              setAuthStatus('authenticated');
          } else {
              setUnauthenticatedState();
          }
      } catch (error: any) {
          console.error("[AUTH] Session validation failed:", error.message);
          // If we get a 401/403 (invalid JWT), wipe everything and show login
          setUnauthenticatedState();
      }
    }, [setUnauthenticatedState]);
    
    useEffect(() => {
        checkSession();
    }, [checkSession]);

    useEffect(() => {
        if (authStatus === 'authenticated' && currentUser && !currentUser.needsOnboarding && !hasFetchedInitialData.current) {
            refreshUsers();
            hasFetchedInitialData.current = true;
        } else if (authStatus === 'unauthenticated') {
            hasFetchedInitialData.current = false;
        }
    }, [authStatus, currentUser, refreshUsers]);

    const handleUpdateProfile = useCallback(async (updatedUser: User) => {
        const oldHandle = currentUser?.handle;
        try {
            const result = await api.updateCurrentUser(updatedUser);
            const returnedUser = result.user;
            if (result.token) {
                localStorage.setItem('youramp-token', result.token);
            }
            if (oldHandle === currentUser?.handle) {
                setCurrentUser(returnedUser);
            } else if (returnedUser.handle === managedProfile?.handle) {
                setManagedProfile(returnedUser);
            }
            setUsers(prevUsers => {
                const filtered = prevUsers.filter(u => u.handle !== oldHandle && u.handle !== returnedUser.handle);
                return [...filtered, returnedUser];
            });
            showToast('Profile updated successfully!');
            return returnedUser;
        } catch (error) {
            throw error;
        }
    }, [currentUser, managedProfile, showToast]);
    
    const handleEndorseUser = useCallback(async (targetUserHandle: string) => {
        if (!currentUser || targetUserHandle === currentUser.handle) return;
        await api.endorseUser(targetUserHandle);
        await refreshUsers();
    }, [currentUser, refreshUsers]);

    const handleUnendorseUser = useCallback(async (targetUserHandle: string) => {
        if (!currentUser) return;
        await api.unendorseUser(targetUserHandle);
        await refreshUsers();
    }, [currentUser, refreshUsers]);

    const handleToggleUserFan = useCallback(async (targetHandle: string) => {
        if (!currentUser) return;
        try {
            await api.toggleFan(targetHandle);
            await refreshUsers();
            showToast("Fan status updated!");
        } catch (error) {
            showToast("Failed to update fan status.");
        }
    }, [currentUser, refreshUsers, showToast]);

    const handleChangePassword = useCallback(async (currentPassword: string, newPassword: string) => {
        try {
            await api.changePassword(currentPassword, newPassword);
            showToast('Password updated successfully!');
            return {};
        } catch (error: any) {
            showToast(`Error: ${error.message}`);
            return { error: error.message };
        }
    }, [showToast]);

    const resendVerification = useCallback(async () => {
        try {
            await api.resendVerification();
            showToast('Verification email sent!');
            return {};
        } catch (error: any) {
            return { error: error.message };
        }
    }, [showToast]);

    const startManaging = useCallback((profile: User) => {
        setManagedProfile(profile);
    }, []);
    
    const stopManaging = useCallback(() => {
        setManagedProfile(null);
    }, []);

    const value = React.useMemo(() => ({ currentUser, managedProfile, users, authStatus, login, loginWithGoogle, loginWithFacebook, register, logout, startManaging, stopManaging, completeOnboarding, handleUpdateProfile, handleEndorseUser, handleUnendorseUser, handleToggleUserFan, handleChangePassword, resendVerification, hasMoreUsers, isLoadingMoreUsers, loadMoreUsers, refreshUsers, setCurrentUser }), [currentUser, managedProfile, users, authStatus, login, loginWithGoogle, loginWithFacebook, register, logout, startManaging, stopManaging, completeOnboarding, handleUpdateProfile, handleEndorseUser, handleUnendorseUser, handleToggleUserFan, handleChangePassword, resendVerification, hasMoreUsers, isLoadingMoreUsers, loadMoreUsers, refreshUsers]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};